import { ApolloClient, NormalizedCacheObject } from '@apollo/client';
import { parse } from 'graphql';
import {
  getQueue,
  removeFromQueue,
  updateQueuedVariables,
  incrementRetryCount,
} from './offlineQueue';
import { applyOperationCacheUpdate } from '../gql/cacheHelpers';
import { classifyOfflineError } from './classifyOfflineError';
import { isSessionChangedError } from '../gql/refreshFailure';
import { isOnlineNow } from './connectivity';
import { belongsToCurrentSession, currentQueueOwner, sessionLeftOperation } from './queueOwner';
import {
  createdSegmentIdFrom,
  remapQueuedMutations,
  syntheticIdFor,
} from './remapPendingSegmentId';
import { remapCompoundFromSegment } from './offlineCompoundQueue';
import { NEVER_QUEUE_OPERATIONS } from './OfflineQueueLink';

/**
 * Qué se ha perdido, en cristiano (2026-08-03).
 *
 * El texto anterior decía SIEMPRE «Una actuación guardada sin conexión…»,
 * viniera de donde viniera la operación. Medido en una prueba real sin
 * cobertura: lo descartado era un TRAMO del recorrido (`CreateRouteSegment`) y
 * el aviso hablaba de una actuación — el operario no podía saber qué había que
 * rehacer. La cola guarda el `operationName`, así que basta con mirarlo.
 *
 * Texto plano a propósito, igual que antes: este manager no vive dentro de
 * React y no tiene `t()` a mano; quien lo pinta es `OfflineBanner`, que sí
 * podría traducir, pero no hay claves para estos casos y crearlas exige el
 * visto bueno del equipo. Se deja acotado y honesto.
 */
export function mensajeDescarte(operationName: string): string {
  const porOperacion: Record<string, string> = {
    CreateRouteSegment:
      'Un tramo iniciado sin conexión no pudo sincronizarse y se ha descartado. Revisa el recorrido: puede que tengas que volver a crearlo.',
    FinishRouteSegment:
      'El cierre de un tramo hecho sin conexión no pudo sincronizarse y se ha descartado. Revisa el recorrido.',
    StartSurveillanceRoute:
      'El inicio de un recorrido hecho sin conexión no pudo sincronizarse y se ha descartado.',
    FinishSurveillanceRoute:
      'La finalización de un recorrido hecha sin conexión no pudo sincronizarse y se ha descartado.',
    LinkExternalOrderToSegment:
      'No se ha vinculado el aviso: ya estaba vinculado a otra OT.',
    SaveSurveillanceRoute:
      'Un cambio en el recorrido hecho sin conexión no pudo sincronizarse y se ha descartado.',
  };
  return (
    porOperacion[operationName] ??
    'Un cambio guardado sin conexión contenía errores y no pudo sincronizarse. Se ha descartado.'
  );
}

export async function syncOfflineQueue(
  apolloClient: ApolloClient<NormalizedCacheObject>
): Promise<{ synced: number; failed: number }> {
  const queue = await getQueue();
  if (queue.length === 0) return { synced: 0, failed: 0 };
  const owner = currentQueueOwner();

  let synced = 0;
  let failed = 0;

  for (const mutation of queue) {
    // Stop syncing if we went offline again
    if (!isOnlineNow()) break;

    // 🔴 Mutations con credenciales (contraseñas o tokens) encoladas por versiones
    // anteriores no son trabajo del operario: guardar credenciales en IndexedDB
    // viola el modelo de seguridad offline. Se tiran sin reproducirlas (spec 2026-10-01 § 5.2).
    if (NEVER_QUEUE_OPERATIONS.has(mutation.operationName)) {
      await removeFromQueue(mutation.id);
      continue;
    }
    // Lo de otro usuario o tenant se aparca: ni se envía con este token, ni
    // gasta intentos, ni se borra (spec 2026-10-01 § 6.2).
    if (!belongsToCurrentSession(mutation.owner, owner)) continue;
    // 🔴 La sesión puede cambiar A MITAD del drenado (switchTenant sin recarga,
    // logout + login de otro): lo que queda del dueño anterior saldría con el
    // token nuevo. Se mira la sesión de AHORA justo antes de enviar y, si ya no
    // es la suya, se corta el drenado sin gastar intentos ni borrar.
    if (!belongsToCurrentSession(mutation.owner, currentQueueOwner())) break;

    try {
      const queryDoc = parse(mutation.query);
      const result = await apolloClient.mutate({
        mutation: queryDoc,
        variables: mutation.variables,
      });
      // Replicar el cache.modify del saveData original. El callback inline
      // `update(cache)` no se serializó al encolar, así que aplicamos el
      // mismo prepend a la lista cached por `operationName` para que la
      // página activa refresque sin perder paginación.
      applyOperationCacheUpdate(
        apolloClient.cache,
        mutation.operationName,
        mutation.variables,
        result?.data,
      );
      // 🔴 Un tramo creado sin cobertura llevaba un id INVENTADO mientras
      // esperaba en la cola, y lo que el operario hiciera después con él —
      // cerrarlo, cambiar de sentido, corregirlo— se encoló apuntando a ese id.
      // Ahora que el servidor ha dado el real, se traduce en todo lo que quede
      // por reproducir; si no, esas operaciones morirían contra un id que no
      // existe y el trabajo del tramo se perdería justo al recuperar la señal.
      const realSegmentId = createdSegmentIdFrom(
        mutation.operationName,
        result?.data
      );
      if (realSegmentId) {
        const pendientes = queue.slice(queue.indexOf(mutation) + 1);
        const reescritas = remapQueuedMutations(
          pendientes,
          syntheticIdFor(mutation.id),
          realSegmentId
        );
        if (reescritas.length > 0) {
          await updateQueuedVariables(reescritas);
          // El bucle recorre el SNAPSHOT tomado al empezar: sin esto, las
          // operaciones siguientes se enviarían con las variables viejas por
          // mucho que la cola persistida ya esté bien.
          reescritas.forEach((reescrita) => {
            const enSnapshot = queue.find((op) => op.id === reescrita.id);
            if (enSnapshot) enSnapshot.variables = reescrita.variables;
          });
        }
        // La básica creada desde ese tramo sin red espera en la cola COMPOUND
        // con el mismo id sintético; se traduce ahí también. La compound se
        // reproduce después de esta, así que su syncAction ya verá el real.
        //
        // Fail-open: el CreateRouteSegment YA se aceptó en el servidor (el
        // `realSegmentId` viene de esa respuesta). Un fallo aquí es un
        // problema de la traducción del id en la cola compound, no del tramo
        // — si lo dejamos propagar al catch de fuera, este bloque trataría la
        // mutation ya sincronizada como fallida y no la retiraría de la cola.
        try {
          await remapCompoundFromSegment(syntheticIdFor(mutation.id), realSegmentId);
        } catch (err) {
          console.warn(
            '[offline] no se pudo traducir el id del tramo en la cola compound; la básica se creará sin vínculo:',
            err
          );
        }
      }

      await removeFromQueue(mutation.id);
      synced++;
    } catch (error: any) {
      // La sesión cambió con el envío en curso (spec § 12.1, § 12.11): la
      // mutation es válida y va sellada con su dueño. Se deja tal cual, sin
      // gastar intento, y se corta: lo que queda es de la sesión anterior. Lo
      // mismo si su dueño cerró sesión mientras salía (spec 2026-10-12 § 10).
      if (isSessionChangedError(error) || sessionLeftOperation(mutation.owner)) {
        failed++;
        break;
      }
      const kind = classifyOfflineError(error);

      if (kind === 'auth') {
        // 🛡️ FIX pérdida de datos: 'Empty AccessToken' / 'Refresh token expire'
        // son GraphQLError, pero la mutation es VÁLIDA — solo falta re-login.
        // NO descartar (antes se borraba → se perdía el trabajo offline). Mantener
        // en cola, avisar para re-autenticar y PARAR (sin sesión el resto falla igual).
        console.warn(
          `[Offline Sync] Error de autenticación al sincronizar. Mantengo la cola y pido re-login.`,
          error
        );
        await incrementRetryCount(mutation.id);
        window.dispatchEvent(
          new CustomEvent('offline-sync-auth-required', {
            detail: {
              message:
                'Sesión caducada. Vuelve a iniciar sesión para sincronizar los cambios guardados sin conexión.',
            },
          })
        );
        failed++;
        break;
      }

      if (kind === 'validation') {
        // Poison pill REAL (validación / 400): nunca tendrá éxito → descartar para
        // evitar bucle infinito.
        console.error(
          `[Offline Sync] Mutación rechazada permanentemente por el servidor (Error de Validación). Eliminando de la cola para evitar bucle infinito.`,
          error
        );
        await removeFromQueue(mutation.id);
        window.dispatchEvent(
          new CustomEvent('offline-sync-error', {
            detail: {
              message: mensajeDescarte(mutation.operationName),
              operationName: mutation.operationName,
            },
          })
        );
        failed++;
      } else {
        // transient: red / 5xx / offline real. Mantener en cola y reintentar.
        console.warn(`[Offline Sync] Error temporal de red/servidor. Reintentando más tarde.`, error);
        await incrementRetryCount(mutation.id);
        failed++;
      }
    }
  }

  return { synced, failed };
}
